import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, Users, DollarSign, Loader2, Shield, UserCog, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

const REP_TYPE_CONFIG = {
  rep1: { label: 'Internal', color: 'bg-blue-100 text-blue-700', icon: Shield },
  rep2: { label: 'Senior/Regional', color: 'bg-purple-100 text-purple-700', icon: UserCog },
  rep3: { label: 'Partner', color: 'bg-amber-100 text-amber-700', icon: Building2 },
};

export default function DeleteRepresentativeDialog({ open, onOpenChange, representative, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [permanentDelete, setPermanentDelete] = useState(false);

  useEffect(() => {
    if (open) setPermanentDelete(false);
  }, [open]);

  const handleDelete = async () => {
    if (!representative) return;
    setLoading(true);
    try {
      const repId = representative.userId || representative._id;
      await adminAPI.deleteRepresentative(repId, permanentDelete);
      toast.success(
        permanentDelete
          ? 'Representative permanently deleted'
          : 'Representative deactivated successfully'
      );
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete representative';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!representative) return null;

  const config = REP_TYPE_CONFIG[representative.role] || REP_TYPE_CONFIG.rep1;
  const TypeIcon = config.icon;
  const hasStudents = (representative.totalStudents || 0) > 0;
  const hasPendingEarnings = (representative.pendingEarnings || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <DialogTitle>Delete Representative</DialogTitle>
          </div>
          <DialogDescription>
            This action will deactivate the representative's account. Their data will be preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Representative Info Card */}
          <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg border border-stone-200">
            <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              {representative.firstName?.[0]}
              {representative.lastName?.[0]}
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {representative.firstName} {representative.lastName}
              </p>
              <p className="text-sm text-gray-600">{representative.email}</p>
              <Badge className={`${config.color} mt-1`}>
                <TypeIcon className="w-3 h-3 mr-1" />
                {config.label}
              </Badge>
            </div>
          </div>

          {/* Warnings */}
          {(hasStudents || hasPendingEarnings) && (
            <div className="space-y-2">
              {hasStudents && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Users className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Active Students</p>
                    <p className="text-xs text-amber-600">
                      This representative has {representative.totalStudents} linked student(s)
                    </p>
                  </div>
                </div>
              )}
              {hasPendingEarnings && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Pending Commissions</p>
                    <p className="text-xs text-amber-600">
                      ${(representative.pendingEarnings || 0).toLocaleString()} in pending earnings
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Permanent Delete Toggle */}
          <label className="flex items-center gap-3 p-3 border border-red-200 rounded-lg cursor-pointer hover:bg-red-50 transition-colors">
            <input
              type="checkbox"
              checked={permanentDelete}
              onChange={(e) => setPermanentDelete(e.target.checked)}
              className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
            />
            <div>
              <p className="text-sm font-medium text-red-700">Permanently delete</p>
              <p className="text-xs text-red-500">
                This cannot be undone. All representative data will be removed.
              </p>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : permanentDelete ? (
              'Permanently Delete'
            ) : (
              'Deactivate Representative'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
